import React, { useState, useEffect } from "react";
import { Grid, Box, Stack } from "@mui/material";
import GenericTable from "../../components/shared/genericTable/genericTable";
import BarChartGeneric from "../../components/shared/barChart/barChart";
import Ec2DetailsModal from "../../components/modals/ec2DetailsModal";
import Ec2HexWorldMap from "../../components/worldHexMap";
import StackedBarChartGeneric from "../../components/shared/stackedBarChartGeneric/stackedBarChartGeneric";
import { callAPI } from "../../functions/api";
import { Ec2ViewSideBar } from "./ec2View.sidebar";
import { regionToCountryCode } from "../../functions/regions";
import { useUser } from "../../functions/userContext";

const Ec2View = () => {
  const [activeEC2Data, setEc2Data] = useState(null);
  const [ec2ByOs, setEc2ByOs] = useState(null);
  const [ec2DataTopCost, setEc2DataTopCost] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [ec2SearchQuery, setEc2SearchQuery] = useState("");
  const [ec2MapData, setEc2MapData] = useState(null);
  const [tenableData, setTenableData] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "score",
    direction: "descending",
    isCustom: true,
  });
  const [loadingEc2Data, setLoadingEc2Data] = useState(true);
  const [loadingEc2ByOs, setLoadingEc2ByOs] = useState(true);
  const [loadingTenableVunData, setLoadingTenableVunData] = useState(true);
  const [instanceIdsWithRecommendations, setInstanceIdsWithRecommendations] = useState([]);

  const { setShowTabs, showTabs } = useUser();
  useEffect(() => {
    setShowTabs(false);
    callAPI("assets/ec2/ec2s", "GET", null, true)
      .then(setEc2Data)
      .catch(console.error)
      .finally(() => {
        setLoadingEc2Data(false);
      });
    callAPI("assets/ec2/ec2sbyos", "GET", null, true)
      .then(setEc2ByOs)
      .catch(console.error)
      .finally(() => {
        setLoadingEc2ByOs(false);
      });
    callAPI("assets/ec2/ec2sbyregion", "GET", null, true).then(setEc2MapData).catch(console.error);

    callAPI("vulnerabilities/tenable/daily", "GET", null, true)
      .then(setTenableData)
      .catch(console.error)
      .finally(() => {
        setLoadingTenableVunData(false);
      });

    callAPI("Insights/instanceIdsWithRecommendations", "GET", null, true)
      .then((data) => setInstanceIdsWithRecommendations(data || []))
      .catch(console.error);
  }, []);

  const handleEce2RowSelection = (row) => {
    setSelectedRowData(row);
    setModalOpen(true);
  };

  const colorMap = {
    PROD: "#70F381", // Brighter green
    PRODUCTION: "#70F381",
    PRO1: "#70F381",
    PRO: "#70F381",

    DEV: "#FFD54F", // Softer yellow with more contrast
    DEVELOPMENT: "#FFD54F",

    QC: "#FF9800", // Bright orange
    UAT: "#CE93D8", // Light purple
    STAGE: "#EF5350", // Bright red

    INFRA: "#4DD0E1", // Bright cyan
    UNKNOWN: "#B0BEC5", // Light grey-blue (neutral)
  };

  const countryCodeToFlagEmoji = (countryCode) => {
    if (!countryCode) return "🌐";
    return countryCode
      .toUpperCase()
      .replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397));
  };

  const calculateComplianceScore = (row) => {
    let score = 100;
    const summary = row.tenableVulnerabilitiesSummary || {};
    const tags = row.tags || {};

    const isExcluded =
      tags.OS?.toLowerCase().includes("amazon") || tags.Image?.toLowerCase().includes("ecs");

    const hasTenable = row.hasTenableAgent;
    const critical = summary.criticalVulnerabilitiesCount ?? 0;
    const high = summary.highVulnerabilitiesCount ?? 0;
    const medium = summary.mediumVulnerabilitiesCount ?? 0;
    const hasAnyVulnerabilities = critical + high + medium > 0;

    // 🔻 1. Only mark down for missing Tenable if no vulnerabilities present and not excluded
    if (!hasTenable && !hasAnyVulnerabilities && !isExcluded) {
      score -= 20;
    }

    // 🔻 2. Deduct for high or critical vulns always (even if Tenable agent is missing)
    if (critical > 0 || high > 0) {
      score -= 20;
    } else if (medium > 0) {
      score -= 10;
    }

    // 🔻 3. Re-creatable deductions
    if (row.isRecreatable === false) score -= 20;
    else if (row.isRecreatable == null) score -= 10;

    // � 4. Missing tags deductions (indicates non-Terraform creation)
    const productName = row.accountName?.toLowerCase() || "";
    const environment = row.environment?.toLowerCase() || "";
    if (!productName || productName === "unknown") score -= 25;
    if (!environment || environment === "unknown") score -= 25;

    // 🔻 5. Optimization opportunity deduction
    const hasRecommendation = instanceIdsWithRecommendations.includes(row.id);
    if (hasRecommendation) score -= 15;

    // 🔺 6. Non-prod environment bonus
    if (row.environment?.toLowerCase() !== "pro") score += 10;

    return Math.max(0, Math.min(100, Math.round(score)));
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 60px)",
        //        p: 2,
        backgroundColor: "background.default",
      }}
    >
      <Grid
        container
        sx={{
          height: "100%",
          paddingLeft: "20px",
          paddingRight: "20px",
          paddingTop: "40px",
          overflow: "hidden",
          flexDirection: "row",
        }}
        spacing={2}
      >
        <Grid item xs={12} height={"60%"}>
          <Grid container sx={{ height: "100%" }} columnSpacing={2} rowSpacing={2}>
            <Grid item xs={12} sx={{ height: "100%" }}>
              <GenericTable
                loading={loadingEc2Data}
                sortConfig={sortConfig}
                setSortConfig={setSortConfig}
                defaultSort={sortConfig}
                showSearch={true}
                discription="EC2s currently active in AWS"
                headerColor="#7E4553"
                title="Active EC2s"
                searchQuery={ec2SearchQuery}
                customColumns={[
                  {
                    name: "Product",
                    align: "left",
                    score: (row) => row.accountName || "",
                    render: (row) => {
                      const productName = row.accountName?.toLowerCase() || "";
                      const isMissing = !productName || productName === "unknown";

                      if (isMissing) {
                        return (
                          <span
                            style={{
                              color: "#f44336",
                              fontWeight: "bold",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            title="Missing Product tag - likely created without Terraform"
                          >
                            🚩 Unknown
                          </span>
                        );
                      }

                      const cleanName = row.accountName
                        .replace(/\b(dev|pro|prod|qc|production)\b/gi, "")
                        .replace(/\s+/g, " ") // collapse multiple spaces
                        .trim();

                      const maxLen = 18;
                      const displayName =
                        cleanName.length > maxLen ? `${cleanName.slice(0, maxLen)}...` : cleanName;

                      return (
                        <span style={{ fontWeight: "bold" }} title={cleanName}>
                          {displayName}
                        </span>
                      );
                    },
                  },
                  {
                    name: "ENV",
                    align: "center",
                    score: (row) => row.environment?.toUpperCase() || "UNKNOWN",
                    render: (row) => {
                      const raw = row.environment || "UNKNOWN";
                      const env = raw.toUpperCase();
                      const isMissing = !raw || raw.toLowerCase() === "unknown";

                      if (isMissing) {
                        return (
                          <span
                            style={{
                              color: "#f44336",
                              fontWeight: "bold",
                              fontSize: "11px",
                              padding: "4px 8px",
                              borderRadius: "12px",
                              background: "rgba(244, 67, 54, 0.1)",
                              border: "1px solid #f44336",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                            title="Missing Environment tag - likely created without Terraform"
                          >
                            🚩 UNKNOWN
                          </span>
                        );
                      }

                      let normalizedEnv = env.replace("INFRASTRUCTURE", "INFRA");

                      // Partial match to map to known color
                      const matchedKey =
                        Object.keys(colorMap).find((key) => normalizedEnv.includes(key)) ||
                        "UNKNOWN";

                      const color = colorMap[matchedKey];

                      return (
                        <span
                          style={{
                            fontWeight: "bold",
                            color,
                            textTransform: "uppercase",
                          }}
                          title={raw}
                        >
                          {matchedKey}
                        </span>
                      );
                    },
                  },
                  {
                    name: "OS",
                    align: "left",
                    score: (row) => row.osName?.toLowerCase() || "zzz_unknown", // For sorting
                    render: (row) => {
                      const os = row.osName || "Unknown OS";
                      const version = row.osVersion ? `${row.osVersion}` : "";
                      return (
                        <Box
                          style={{ display: "flex", alignItems: "left", flexDirection: "column" }}
                        >
                          <span style={{ fontWeight: "bold", color: "#F9CA00" }}>{os}</span>
                          <span style={{ fontSize: "10px" }}>{version}</span>
                        </Box>
                      );
                    },
                  },
                  {
                    name: "Region",
                    align: "center",
                    score: (row) => row.regionDisplayName || "",
                    render: (row) => {
                      const regionName = row.regionDisplayName || "Unknown";
                      const systemName = row.regionSystemName?.toLowerCase();
                      const countryCode = regionToCountryCode[systemName];

                      let flagSrc = "";
                      try {
                        flagSrc = require(`../../assets/flags/${countryCode.toLowerCase()}.png`);
                      } catch (err) {
                        flagSrc = null;
                      }

                      return (
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                          }}
                          title={regionName}
                        >
                          {flagSrc && (
                            <img
                              src={flagSrc}
                              alt={regionName?.toUpperCase()}
                              width="24"
                              height="18"
                              style={{ borderRadius: "2px", objectFit: "cover" }}
                            />
                          )}
                        </span>
                      );
                    },
                  },
                  {
                    name: "Owner",
                    align: "center",
                    score: (row) => row.tags?.["Service Owner"] || "Unknown",
                    render: (row) => {
                      const owner = row.tags?.["Service Owner"] || "Unknown";
                      return (
                        <span
                          style={{
                            fontWeight: "bold",
                          }}
                          title={owner}
                        >
                          {owner.length > 18 ? owner.slice(0, 18) + "…" : owner}
                        </span>
                      );
                    },
                  },
                  {
                    name: "Tenable",
                    align: "center",
                    score: (row) => {
                      const hasTenable = row.hasTenableAgent || row.tenableLastScan;
                      return hasTenable ? 1 : 0;
                    },
                    render: (row) => {
                      const hasTenableAgent = row.hasTenableAgent || row.tenableLastScan;
                      const tags = row.tags || {};
                      const isExcluded =
                        tags.OS?.toLowerCase().includes("amazon") ||
                        tags.Image?.toLowerCase().includes("ecs");

                      if (isExcluded) {
                        return (
                          <span
                            style={{
                              color: "#B0BEC5",
                              fontWeight: "bold",
                              fontSize: "11px",
                            }}
                            title="Tenable not required for this OS/Image type"
                          >
                            N/A
                          </span>
                        );
                      }

                      return hasTenableAgent ? (
                        <span
                          style={{
                            color: "#4caf50",
                            fontWeight: "bold",
                            fontSize: "11px",
                          }}
                          title="Tenable agent installed"
                        >
                          ✓ Installed
                        </span>
                      ) : (
                        <span
                          style={{
                            color: "#f44336",
                            fontWeight: "bold",
                            fontSize: "11px",
                          }}
                          title="Tenable agent not installed"
                        >
                          ✗ Missing
                        </span>
                      );
                    },
                  },
                  {
                    name: "Vulnerabilities",
                    align: "center",
                    score: (row) => {
                      const summary = row.tenableVulnerabilitiesSummary || {};
                      // Assign weights: Critical = 100, High = 50, Medium = 20, Low = 10
                      return (
                        (summary.criticalVulnerabilitiesCount || 0) * 100 +
                        (summary.highVulnerabilitiesCount || 0) * 50 +
                        (summary.mediumVulnerabilitiesCount || 0) * 20 +
                        (summary.lowVulnerabilitiesCount || 0) * 10
                      );
                    },
                    render: (row) => {
                      const summary = row.tenableVulnerabilitiesSummary || {};

                      const levels = [
                        {
                          label: "Critical",
                          count: summary.criticalVulnerabilitiesCount,
                          color: "#BA1650",
                        },
                        {
                          label: "High",
                          count: summary.highVulnerabilitiesCount,
                          color: "#F97316",
                        },
                        {
                          label: "Medium",
                          count: summary.mediumVulnerabilitiesCount,
                          color: "#F9CA00",
                        },
                        { label: "Low", count: summary.lowVulnerabilitiesCount, color: "#70C930" },
                      ];

                      const hasAny = levels.some((lvl) => lvl.count > 0);

                      return hasAny ? (
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            flexWrap: "wrap",
                            justifyContent: "center",
                          }}
                        >
                          {levels.map(({ label, count, color }) =>
                            count > 0 ? (
                              <span
                                key={label}
                                title={`${count} ${label}`}
                                style={{
                                  background: color,
                                  color: "#000",
                                  fontWeight: "bold",
                                  fontSize: "12px",
                                  padding: "2px 6px",
                                  borderRadius: "10px",
                                }}
                              >
                                {label[0]}:{count}
                              </span>
                            ) : null
                          )}
                        </div>
                      ) : (
                        <span style={{ color: "#70C930", fontWeight: "bold" }}>✅ None</span>
                      );
                    },
                  },
                  {
                    name: "Optimize",
                    align: "center",
                    score: (row) => {
                      const hasRecommendation = instanceIdsWithRecommendations.includes(row.id);
                      return hasRecommendation ? 1 : 0;
                    },
                    render: (row) => {
                      const hasRecommendation = instanceIdsWithRecommendations.includes(row.id);
                      return hasRecommendation ? (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            background: "#5d4e37",
                            color: "#ffd54f",
                            fontWeight: "bold",
                            fontSize: "11px",
                            padding: "4px 8px",
                            borderRadius: "12px",
                          }}
                          title="Click to view optimization recommendations"
                        >
                          💡 Recommendations
                        </span>
                      ) : (
                        <span
                          style={{
                            color: "#4caf50",
                            fontWeight: "bold",
                            fontSize: "11px",
                          }}
                        >
                          ✓ Optimized
                        </span>
                      );
                    },
                  },
                  {
                    name: "Score",
                    align: "center",
                    score: (row) => calculateComplianceScore(row),

                    render: (row) => {
                      const score = calculateComplianceScore(row);
                      const summary = row.tenableVulnerabilitiesSummary || {};
                      const tags = row.tags || {};
                      const isExcluded =
                        tags.OS?.toLowerCase().includes("amazon") ||
                        tags.Image?.toLowerCase().includes("ecs");

                      const critical = summary.criticalVulnerabilitiesCount || 0;
                      const high = summary.highVulnerabilitiesCount || 0;
                      const medium = summary.mediumVulnerabilitiesCount || 0;
                      const hasAnyVulnerabilities = critical > 0 || high > 0 || medium > 0;
                      const hasTenableAgent = row.hasTenableAgent;

                      const productName = row.accountName?.toLowerCase() || "";
                      const environment = row.environment?.toLowerCase() || "";
                      const hasRecommendation = instanceIdsWithRecommendations.includes(row.id);
                      const missingProduct = !productName || productName === "unknown";
                      const missingEnv = !environment || environment === "unknown";

                      const tooltip = [
                        // Exclusion
                        isExcluded && "ℹ️ Excluded OS/Image – Agent not required",

                        // Tenable Agent logic
                        !hasTenableAgent && !hasAnyVulnerabilities && !isExcluded
                          ? "❌ No Tenable Agent (-20%)"
                          : hasTenableAgent
                            ? "✅ Has Tenable Agent"
                            : isExcluded
                              ? "🤷 Agent not required (excluded)"
                              : "✅ Vulnerabilities found – agent penalty skipped",

                        // Recreatable
                        row.isRecreatable === false
                          ? "❌ Not Recreatable (-20%)"
                          : row.isRecreatable === true
                            ? "✅ Is Recreatable"
                            : "🤷 Recreatable Unknown (-10%)",

                        // Vulnerability Penalty (always applies)
                        critical > 0
                          ? "❌ Critical Vulnerabilities (-20%)"
                          : high > 0
                            ? "⚠️ High Vulnerabilities (-20%)"
                            : medium > 0
                              ? "⚠️ Medium Vulnerabilities (no penalty)"
                              : "✅ No Critical/High Vulnerabilities",

                        // Missing tags
                        missingProduct ? "🚨 Missing Product Tag (-25%)" : "✅ Has Product Tag",
                        missingEnv ? "🚨 Missing Environment Tag (-25%)" : "✅ Has Environment Tag",

                        // Optimization
                        hasRecommendation
                          ? "💡 Optimization Available (-15%)"
                          : "✅ No Optimization Needed",

                        // Environment
                        row.environment?.toLowerCase() !== "pro"
                          ? "📦 Lower Environment (+10%)"
                          : "🏢 Production Environment (no bonus)",

                        // Final Score
                        `📊 Final Score: ${score}%`,
                      ]
                        .filter(Boolean)
                        .join("\n");

                      let barColor = "#BA1650";
                      if (score >= 75) barColor = "#70C930";
                      else if (score >= 50) barColor = "#F9CA00";

                      return (
                        <div style={{ width: "100%", padding: "0 5px", cursor: "pointer" }}>
                          <div
                            title={tooltip}
                            style={{
                              height: "16px",
                              width: "100%",
                              backgroundColor: "#404040",
                              borderRadius: "8px",
                              overflow: "hidden",
                              position: "relative",
                              fontSize: "12px",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: score < 15 ? "flex-end" : "center",
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                width: `${score}%`,
                                backgroundColor: barColor,
                                transition: "width 0.3s ease",
                                position: "absolute",
                                top: 0,
                                left: 0,
                              }}
                            />
                            <span
                              style={{
                                zIndex: 1,
                                width: "100%",
                                textAlign: "center",
                                color: "#fff",
                                fontWeight: "bold",
                              }}
                            >
                              {score}%
                            </span>
                          </div>
                        </div>
                      );
                    },
                  },
                ]}
                showActionColumn={true}
                onActionClick={handleEce2RowSelection}
                data={activeEC2Data}
              ></GenericTable>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} height={"40%"}>
          <Grid container sx={{ height: "100%" }} columnSpacing={2} rowSpacing={2}>
            <Grid item xs={4} sx={{ height: "100%" }}>
              <BarChartGeneric
                showBarIcon={true}
                loading={loadingEc2ByOs}
                showCount={true}
                title="EC2s by OS"
                columnColorMapping={[{ name: "Other OS", color: "#7E4553" }]}
                data={ec2ByOs}
                onBarClick={(env) => {
                  setEc2SearchQuery({
                    osName: { operator: "equals", value: env[0].name },
                    ...(env[1].toLowerCase() !== "all" && {
                      environment: {
                        operator: "contains",
                        value: env[1],
                      },
                    }),
                  });
                }}
              ></BarChartGeneric>
            </Grid>
            <Grid item xs={4} sx={{ height: "100%" }}>
              <StackedBarChartGeneric
                LegendOnLeft={true}
                colorOverrides={{
                  Critical: "#BA1650", // red
                  High: "#F9CA00", // yellow
                  Medium: "#E97F03", // orange
                }}
                loading={loadingTenableVunData}
                title={"Tenable Vulnerabilities"}
                description={"Open EC2 Vulnerabilities last 7 days"}
                data={tenableData}
              ></StackedBarChartGeneric>
            </Grid>
            <Grid item xs={4} sx={{ height: "100%" }}>
              <Ec2HexWorldMap
                instanceData={ec2MapData}
                onHexClick={(selection) => {
                  const account = Object.keys(selection.accounts)[0];
                  setEc2SearchQuery(
                    account
                      ? {
                          regionDisplayName: {
                            operator: "contains",
                            value: selection.regionText,
                          },

                          accountName: {
                            operator: "contains",
                            value: account,
                          },
                        }
                      : {
                          regionDisplayName: {
                            operator: "contains",
                            value: selection.regionText,
                          },
                        }
                  );
                }}
              ></Ec2HexWorldMap>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Ec2ViewSideBar></Ec2ViewSideBar>

      <Ec2DetailsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        data={selectedRowData}
      />
    </Box>
  );
};

const modalStyles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
  },
  paper: {
    width: "70%",
    maxWidth: "800px",
    padding: "20px",
    backgroundColor: "#303030",
    color: "#fff",
    borderRadius: "8px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 1,
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "bold",
    mt: 2,
    color: "#F9CA00",
  },
  text: {
    fontSize: "14px",
    color: "#eee",
    mb: 1,
  },
  closeButton: {
    color: "#fff",
    backgroundColor: "#BA1650",
    ":hover": {
      backgroundColor: "#9D419B",
    },
  },
};

export default Ec2View;
